import { tenantPath } from "helpers/tenant_path";
import {
  ABSENCE_PATH,
  COMMENTS_PATH,
  COMMUNITY_MEMBER_PROFILES_PATH,
  CREATE_ABSENCE_PATH,
  PERFORMANCE_FIELDS_PATH,
  PERFORMANCE_RECORDS_PATH,
  TAGGERS_PATH,
  FEEDBACK_PATH,
  CREATE_FEEDBACK_PATH,
  ONBOARDING_PATH,
  COACHING_PATH,
} from "packs/client_app/config";
import { getHeaders } from "packs/client_app/config/request_headers";

import {
  ADMIN_ABSENCE_PATH,
  ADMIN_COACHING_PATH,
  ADMIN_COMMENT_PATH,
  ADMIN_COMMUNITY_MEMBER_PROFILES_PATH,
  ADMIN_ONBOARDING_PATH,
  ADMIN_PERFORMANCE_FIELDS_PATH,
  ADMIN_PERFORMANCE_RECORDS_PATH,
  ADMIN_TAGGERS_PATH,
} from "@/organization_app/config";

export const getCommunityMemberProfiles = (positionId: string, inAdmin?) => {
  let path = tenantPath(COMMUNITY_MEMBER_PROFILES_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_COMMUNITY_MEMBER_PROFILES_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

export const getOnboardingChecklist = (positionId: string, inAdmin?) => {
  let path = tenantPath(ONBOARDING_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_ONBOARDING_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

export const getCoachingChecklist = (positionId: string, inAdmin?) => {
  let path = tenantPath(COACHING_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_COACHING_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

interface UpdateParams {
  positionId: string;
  onboarding_checklist_id: string;
  completed_at?: string;
  completed?: boolean;
  inAdmin?: boolean;
}
export const updateOnboardingChecklist = ({
  positionId,
  onboarding_checklist_id,
  completed_at,
  completed,
  inAdmin = false,
}: UpdateParams) => {
  let path = tenantPath(ONBOARDING_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_ONBOARDING_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({
      position_id: positionId,
      onboarding_checklist_id: onboarding_checklist_id,
      completed_at: completed_at,
      completed: completed,
    }),
  });
};

export const updateFocus = (positionId: string, focus: string, inAdmin?) => {
  let path = tenantPath(COMMUNITY_MEMBER_PROFILES_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_COMMUNITY_MEMBER_PROFILES_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({
      focus: focus,
    }),
  });
};

export const createAbsence = (
  positionId: string,
  formData: FormData,
  inAdmin?
) => {
  let path = tenantPath(CREATE_ABSENCE_PATH(positionId));
  if (inAdmin) {
    path = CREATE_ABSENCE_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: {
      "X-CSRF-Token": document
        .querySelector("meta[name='csrf-token']")
        .getAttribute("content"),
    },
    body: formData,
  });
};
export const updateCoaching = (
  positionId: string,
  formData: FormData,
  inAdmin?
) => {
  let path = tenantPath(COACHING_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_COACHING_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: {
      "X-CSRF-Token": document
        .querySelector("meta[name='csrf-token']")
        .getAttribute("content"),
    },
    body: formData,
  });
};

export const getAbsence = (positionId: string, inAdmin?) => {
  let path = tenantPath(ABSENCE_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_ABSENCE_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

export const createFeedback = (
  positionId: string,
  formData: FormData,
  inAdmin?
) => {
  let path = tenantPath(CREATE_FEEDBACK_PATH(positionId));
  if (inAdmin) {
    path = CREATE_FEEDBACK_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: {
      "X-CSRF-Token": document
        .querySelector("meta[name='csrf-token']")
        .getAttribute("content"),
    },
    body: formData,
  });
};

export const getFeedbacks = (positionId: string, inAdmin?) => {
  let path = tenantPath(FEEDBACK_PATH(positionId));
  if (inAdmin) {
    path = FEEDBACK_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};
export const getPerformanceRecords = (positionId: string, inAdmin?) => {
  let path = tenantPath(PERFORMANCE_RECORDS_PATH(positionId));
  if (inAdmin) {
    path = PERFORMANCE_RECORDS_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

export const savePerformanceRecords = (
  positionId: string,
  date: string,
  id: string | null,
  metrics: object,
  inAdmin?
) => {
  let path = tenantPath(PERFORMANCE_RECORDS_PATH(positionId));
  if (inAdmin) {
    path = ADMIN_PERFORMANCE_RECORDS_PATH(positionId);
  }
  return fetch(path, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({
      record_id: id,
      date,
      metrics,
    }),
  });
};

export const getPerformanceFields = (positionId: string, inAdmin?) => {
  let path = tenantPath(PERFORMANCE_FIELDS_PATH);
  if (inAdmin) {
    path = ADMIN_PERFORMANCE_FIELDS_PATH(positionId);
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};

export const getComments = ({ recordId, recordType, inAdmin }) => {
  const query = `record_id=${recordId}&record_type=${recordType}`;

  let path = COMMENTS_PATH;

  if (inAdmin) {
    path = ADMIN_COMMENT_PATH;
  }
  return fetch(tenantPath(`${path}?${query}`), {
    method: "GET",
    headers: getHeaders(),
  });
};

export const sendComment = ({
  recordId,
  recordType,
  content,
  mentions,
  url,
  inAdmin = false,
}) => {
  const path = inAdmin ? ADMIN_COMMENT_PATH : tenantPath(COMMENTS_PATH);
  return fetch(path, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      record_id: recordId,
      url: url,
      record_type: recordType,
      content: content,
      mentions: mentions,
    }),
  });
};

export const getTaggers = (positionId, inAdmin?) => {
  let path = `${tenantPath(TAGGERS_PATH)}?position_id=${positionId}`;
  if (inAdmin) {
    path = `${ADMIN_TAGGERS_PATH}?position_id=${positionId}`;
  }
  return fetch(path, {
    method: "GET",
    headers: getHeaders(),
  });
};
